import { clamp } from '@game/scenes/characterPrototype/math';

export function circleIntersectsRect(
	cx: number,
	cy: number,
	cr: number,
	rx: number,
	ry: number,
	rw: number,
	rh: number
) {
	const closestX = clamp(cx, rx, rx + rw);
	const closestY = clamp(cy, ry, ry + rh);
	const dx = cx - closestX;
	const dy = cy - closestY;
	return dx * dx + dy * dy <= cr * cr;
}

export function circleRectClosestPoint(cx: number, cy: number, rx: number, ry: number, rw: number, rh: number) {
	return {
		x: clamp(cx, rx, rx + rw),
		y: clamp(cy, ry, ry + rh)
	};
}

export function pointInRect(px: number, py: number, rx: number, ry: number, rw: number, rh: number) {
	return px >= rx && px <= rx + rw && py >= ry && py <= ry + rh;
}

export function segmentsIntersect(
	ax: number,
	ay: number,
	bx: number,
	by: number,
	cx: number,
	cy: number,
	dx: number,
	dy: number
) {
	const abx = bx - ax;
	const aby = by - ay;
	const acx = cx - ax;
	const acy = cy - ay;
	const adx = dx - ax;
	const ady = dy - ay;
	const cdx = dx - cx;
	const cdy = dy - cy;
	const cabx = ax - cx;
	const caby = ay - cy;
	const cbx = bx - cx;
	const cby = by - cy;

	const cross1 = abx * acy - aby * acx;
	const cross2 = abx * ady - aby * adx;
	const cross3 = cdx * caby - cdy * cabx;
	const cross4 = cdx * cby - cdy * cbx;

	// Proper intersection
	if ((cross1 > 0 && cross2 < 0) || (cross1 < 0 && cross2 > 0)) {
		if ((cross3 > 0 && cross4 < 0) || (cross3 < 0 && cross4 > 0)) return true;
	}

	// Colinear / touching cases
	const eps = 1e-6;
	const onSeg = (px: number, py: number, x1: number, y1: number, x2: number, y2: number) => {
		return (
			Math.min(x1, x2) - eps <= px &&
			px <= Math.max(x1, x2) + eps &&
			Math.min(y1, y2) - eps <= py &&
			py <= Math.max(y1, y2) + eps
		);
	};
	if (Math.abs(cross1) <= eps && onSeg(cx, cy, ax, ay, bx, by)) return true;
	if (Math.abs(cross2) <= eps && onSeg(dx, dy, ax, ay, bx, by)) return true;
	if (Math.abs(cross3) <= eps && onSeg(ax, ay, cx, cy, dx, dy)) return true;
	if (Math.abs(cross4) <= eps && onSeg(bx, by, cx, cy, dx, dy)) return true;
	return false;
}

export function segmentIntersectsRect(
	ax: number,
	ay: number,
	bx: number,
	by: number,
	rx: number,
	ry: number,
	rw: number,
	rh: number
) {
	if (pointInRect(ax, ay, rx, ry, rw, rh) || pointInRect(bx, by, rx, ry, rw, rh)) return true;
	const x1 = rx;
	const y1 = ry;
	const x2 = rx + rw;
	const y2 = ry + rh;
	return (
		segmentsIntersect(ax, ay, bx, by, x1, y1, x2, y1) ||
		segmentsIntersect(ax, ay, bx, by, x2, y1, x2, y2) ||
		segmentsIntersect(ax, ay, bx, by, x2, y2, x1, y2) ||
		segmentsIntersect(ax, ay, bx, by, x1, y2, x1, y1)
	);
}


